import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, CheckIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { Organisation } from '../types';
import {
  PricingPlan,
  CreateInvoiceRequest,
  PaymentTermType,
  PaymentTermPreset,
  calculateDueDate,
} from '../shared';
import { api } from '../utils/api';

interface InvoiceCreateProps {
  organisationId: string;
}

interface LineItemForm {
  description: string;
  quantity: number | '';
  unitPrice: number | '';
  taxRate?: number | '';
  discountPercentage?: number | '';
  costCentre?: string;
  poNumber?: string;
  vendorCode?: string;
}

interface BillingAddressForm {
  companyName: string;
  contactName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
  vatNumber?: string;
}

type Currency = 'GBP' | 'USD' | 'EUR';

interface FormState {
  selectedPlan?: string;
  currency: Currency;
  purchaseOrderNumber?: string;
  billingAddress: BillingAddressForm;
  lineItems: LineItemForm[];
  paymentTermsId?: string;
  paymentTermType?: PaymentTermType;
  customDaysUntilDue?: number;
  dueDate?: string;
  notes?: string;
  customerNotes?: string;
}

const STEP_TITLES = ['Organisation & Plan', 'Billing Address', 'Line Items', 'Terms & Notes'];

const emptyLineItem = (): LineItemForm => ({
  description: '',
  quantity: 1,
  unitPrice: '',
});

const initialState = (): FormState => ({
  currency: 'GBP',
  billingAddress: {
    companyName: '',
    addressLine1: '',
    city: '',
    postalCode: '',
    country: '',
  },
  lineItems: [],
});

const InvoiceCreate: React.FC<InvoiceCreateProps> = ({ organisationId }) => {
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [organisationDetails, setOrganisationDetails] = useState<Organisation | null>(null);
  const [paymentTermPresets, setPaymentTermPresets] = useState<PaymentTermPreset[]>([]);
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;
    Promise.all([api.getOrganisation(organisationId), api.getPlans(), api.getPaymentTermPresets()])
      .then(([organisation, retrievedPlans, paymentPresets]) => {
        if (cancelled) return;
        setOrganisationDetails(organisation);
        setPlans(retrievedPlans);
        setPaymentTermPresets(paymentPresets);
        if (organisation) {
          setForm((prev) => ({
            ...prev,
            billingAddress: {
              ...prev.billingAddress,
              companyName: prev.billingAddress.companyName || organisation.name,
            },
          }));
        }
      })
      .catch((err) => {
        if (!cancelled) showError('Load Failed', `Failed to load data: ${(err as Error).message}`);
      });
    return () => {
      cancelled = true;
    };
  }, [organisationId, showError]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateBillingField = <K extends keyof BillingAddressForm>(
    key: K,
    value: BillingAddressForm[K]
  ) => {
    setForm((prev) => ({
      ...prev,
      billingAddress: { ...prev.billingAddress, [key]: value },
    }));
  };

  const updateLineItem = <K extends keyof LineItemForm>(
    index: number,
    key: K,
    value: LineItemForm[K]
  ) => {
    setForm((prev) => {
      const next = [...prev.lineItems];
      const current = next[index];
      if (current) {
        next[index] = { ...current, [key]: value };
      }
      return { ...prev, lineItems: next };
    });
  };

  const addLineItem = () => {
    setForm((prev) => ({ ...prev, lineItems: [...prev.lineItems, emptyLineItem()] }));
  };

  const removeLineItem = (index: number) => {
    setForm((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  const validateStep = (step: number): Record<string, string> => {
    const stepErrors: Record<string, string> = {};
    if (step === 1) {
      if (!form.billingAddress.companyName.trim())
        stepErrors['billingAddress.companyName'] = 'Company name is required';
      if (!form.billingAddress.addressLine1.trim())
        stepErrors['billingAddress.addressLine1'] = 'Address is required';
      if (!form.billingAddress.city.trim()) stepErrors['billingAddress.city'] = 'City is required';
      if (!form.billingAddress.postalCode.trim())
        stepErrors['billingAddress.postalCode'] = 'Postal code is required';
      if (!form.billingAddress.country.trim())
        stepErrors['billingAddress.country'] = 'Country is required';
    }
    if (step === 2) {
      form.lineItems.forEach((item, idx) => {
        if (!item.description.trim())
          stepErrors[`lineItems.${idx}.description`] = 'Description is required';
        if (item.quantity === '' || (typeof item.quantity === 'number' && item.quantity < 1))
          stepErrors[`lineItems.${idx}.quantity`] = 'Quantity must be at least 1';
        if (item.unitPrice === '' || (typeof item.unitPrice === 'number' && item.unitPrice < 0))
          stepErrors[`lineItems.${idx}.unitPrice`] = 'Unit price is required';
      });
    }
    if (step === 3) {
      if (form.paymentTermType === PaymentTermType.CUSTOM) {
        if (
          form.customDaysUntilDue === undefined ||
          form.customDaysUntilDue < 0 ||
          form.customDaysUntilDue > 365
        ) {
          stepErrors['paymentTerms.daysUntilDue'] =
            'Days until due is required (0–365) for custom terms';
        }
      }
    }
    return stepErrors;
  };

  const next = () => {
    const stepErrors = validateStep(currentStep);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length === 0) setCurrentStep((s) => s + 1);
  };

  const prev = () => setCurrentStep((s) => Math.max(0, s - 1));

  const onPaymentTermChange = (paymentTermsId: string) => {
    const preset = paymentTermPresets.find((p) => p.id === paymentTermsId);
    setForm((prev) => ({
      ...prev,
      paymentTermsId,
      paymentTermType: preset?.id,
      customDaysUntilDue:
        preset?.id === PaymentTermType.CUSTOM ? prev.customDaysUntilDue : undefined,
      dueDate:
        preset && preset.id !== PaymentTermType.CUSTOM
          ? formatDateInput(calculateDueDate(new Date(), preset.netDays))
          : prev.dueDate,
    }));
  };

  const onCustomDaysChange = (value: number) => {
    setForm((prev) => ({
      ...prev,
      customDaysUntilDue: value,
      dueDate: formatDateInput(calculateDueDate(new Date(), value)),
    }));
  };

  const handleSubmit = async () => {
    const allErrors = { ...validateStep(1), ...validateStep(2), ...validateStep(3) };
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      showError('Validation Failed', 'Please fix the errors before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const payload: CreateInvoiceRequest = {
        organisationId,
        currency: form.currency,
        dueDate: form.dueDate ? new Date(form.dueDate) : undefined,
        purchaseOrderNumber: form.purchaseOrderNumber || undefined,
        billingAddress: form.billingAddress,
        lineItems: form.lineItems.map((item) => ({
          description: item.description,
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          taxRate:
            item.taxRate !== '' && item.taxRate !== undefined ? Number(item.taxRate) : undefined,
          discountPercentage:
            item.discountPercentage !== '' && item.discountPercentage !== undefined
              ? Number(item.discountPercentage)
              : undefined,
          costCentre: item.costCentre || undefined,
          poNumber: item.poNumber || undefined,
          vendorCode: item.vendorCode || undefined,
        })),
        paymentTermsId: form.paymentTermsId,
        notes: form.notes,
        customerNotes: form.customerNotes,
      };
      const created = await api.createInvoice(organisationId, payload);
      showSuccess('Invoice Created', 'Invoice created successfully');
      navigate(`/invoices/${organisationId}/${created.id}`);
    } catch (err) {
      showError(
        'Create Failed',
        `Failed to create invoice: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const stepHeader = useMemo(
    () => (
      <ol className="flex items-center w-full mb-6">
        {STEP_TITLES.map((title, idx) => {
          const isLast = idx === STEP_TITLES.length - 1;
          const finished = idx < currentStep;
          const active = idx === currentStep;
          return (
            <li key={title} className={`flex items-center ${isLast ? '' : 'flex-1'}`}>
              <span
                className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold shrink-0 ${
                  finished
                    ? 'bg-green-500 text-white'
                    : active
                      ? 'bg-primary text-primary-content'
                      : 'bg-base-200 text-base-content/60'
                }`}
              >
                {finished ? <CheckIcon className="h-4 w-4" /> : idx + 1}
              </span>
              <span
                className={`ml-2 text-sm ${
                  active ? 'text-base-content font-medium' : 'text-base-content/60'
                }`}
              >
                {title}
              </span>
              {!isLast && <div className="flex-1 mx-3 h-px bg-base-300" />}
            </li>
          );
        })}
      </ol>
    ),
    [currentStep]
  );

  return (
    <div className="space-y-4">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-base-content mb-6">Create Invoice</h1>
        {stepHeader}

        <div className="min-h-[420px]">
          {currentStep === 0 && (
            <StepOrganisationPlan
              form={form}
              plans={plans}
              organisationName={organisationDetails?.name}
              onChange={updateField}
            />
          )}
          {currentStep === 1 && (
            <StepBillingAddress
              billing={form.billingAddress}
              errors={errors}
              onChange={updateBillingField}
            />
          )}
          {currentStep === 2 && (
            <StepLineItems
              items={form.lineItems}
              errors={errors}
              onAdd={addLineItem}
              onRemove={removeLineItem}
              onChange={updateLineItem}
            />
          )}
          {currentStep === 3 && (
            <StepTerms
              form={form}
              presets={paymentTermPresets}
              errors={errors}
              onPaymentTermChange={onPaymentTermChange}
              onCustomDaysChange={onCustomDaysChange}
              onChange={updateField}
            />
          )}
        </div>

        <div className="border-t border-base-300 mt-6 pt-4 flex justify-end gap-2">
          {currentStep > 0 && (
            <button type="button" onClick={prev} className="btn btn-ghost btn-sm">
              Previous
            </button>
          )}
          {currentStep < STEP_TITLES.length - 1 && (
            <button type="button" onClick={next} className="btn btn-primary btn-sm gap-1">
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          )}
          {currentStep === STEP_TITLES.length - 1 && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="btn btn-primary btn-sm"
            >
              {submitting ? 'Creating…' : 'Create Invoice'}
            </button>
          )}
        </div>
      </div>

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

// ── Step 0 ──────────────────────────────────────────────────────────────

function StepOrganisationPlan({
  form,
  plans,
  organisationName,
  onChange,
}: {
  form: FormState;
  plans: PricingPlan[];
  organisationName: string | undefined;
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-base-content">Organisation Details</h2>
        <p className="text-sm text-base-content/70 mt-1">
          Creating invoice for: <strong>{organisationName ?? '—'}</strong>
        </p>
      </div>

      <Field label="Select Organisation Plan" id="selected-plan">
        <select
          id="selected-plan"
          className="select select-bordered select-sm w-full max-w-md"
          value={form.selectedPlan ?? ''}
          onChange={(e) => onChange('selectedPlan', e.target.value || undefined)}
        >
          <option value="">Select a plan (optional)</option>
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.displayName} — £
              {typeof plan.price === 'number' ? plan.price : plan.price.amount}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Currency" id="currency">
        <select
          id="currency"
          className="select select-bordered select-sm w-full max-w-md"
          value={form.currency}
          onChange={(e) => onChange('currency', e.target.value as Currency)}
        >
          <option value="GBP">GBP (£)</option>
          <option value="USD">USD ($)</option>
          <option value="EUR">EUR (€)</option>
        </select>
      </Field>

      <Field label="Purchase Order Number" id="po-number">
        <input
          id="po-number"
          type="text"
          className="input input-bordered input-sm w-full max-w-md"
          placeholder="Enter PO number (optional)"
          value={form.purchaseOrderNumber ?? ''}
          onChange={(e) => onChange('purchaseOrderNumber', e.target.value)}
        />
      </Field>
    </div>
  );
}

// ── Step 1 ──────────────────────────────────────────────────────────────

function StepBillingAddress({
  billing,
  errors,
  onChange,
}: {
  billing: BillingAddressForm;
  errors: Record<string, string>;
  onChange: <K extends keyof BillingAddressForm>(key: K, value: BillingAddressForm[K]) => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-base-content">Billing Address</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Company Name"
          id="company-name"
          required
          error={errors['billingAddress.companyName']}
        >
          <input
            id="company-name"
            type="text"
            className="input input-bordered input-sm w-full"
            value={billing.companyName}
            onChange={(e) => onChange('companyName', e.target.value)}
          />
        </Field>
        <Field label="Contact Name" id="contact-name">
          <input
            id="contact-name"
            type="text"
            className="input input-bordered input-sm w-full"
            value={billing.contactName ?? ''}
            onChange={(e) => onChange('contactName', e.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Address Line 1"
        id="address-1"
        required
        error={errors['billingAddress.addressLine1']}
      >
        <input
          id="address-1"
          type="text"
          className="input input-bordered input-sm w-full"
          value={billing.addressLine1}
          onChange={(e) => onChange('addressLine1', e.target.value)}
        />
      </Field>

      <Field label="Address Line 2" id="address-2">
        <input
          id="address-2"
          type="text"
          className="input input-bordered input-sm w-full"
          value={billing.addressLine2 ?? ''}
          onChange={(e) => onChange('addressLine2', e.target.value)}
        />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="City" id="city" required error={errors['billingAddress.city']}>
          <input
            id="city"
            type="text"
            className="input input-bordered input-sm w-full"
            value={billing.city}
            onChange={(e) => onChange('city', e.target.value)}
          />
        </Field>
        <Field label="State/County" id="state">
          <input
            id="state"
            type="text"
            className="input input-bordered input-sm w-full"
            value={billing.state ?? ''}
            onChange={(e) => onChange('state', e.target.value)}
          />
        </Field>
        <Field
          label="Postal Code"
          id="postal-code"
          required
          error={errors['billingAddress.postalCode']}
        >
          <input
            id="postal-code"
            type="text"
            className="input input-bordered input-sm w-full"
            value={billing.postalCode}
            onChange={(e) => onChange('postalCode', e.target.value)}
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Country" id="country" required error={errors['billingAddress.country']}>
          <input
            id="country"
            type="text"
            className="input input-bordered input-sm w-full"
            value={billing.country}
            onChange={(e) => onChange('country', e.target.value)}
          />
        </Field>
        <Field label="VAT Number" id="vat-number">
          <input
            id="vat-number"
            type="text"
            className="input input-bordered input-sm w-full"
            value={billing.vatNumber ?? ''}
            onChange={(e) => onChange('vatNumber', e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}

// ── Step 2 ──────────────────────────────────────────────────────────────

function StepLineItems({
  items,
  errors,
  onAdd,
  onRemove,
  onChange,
}: {
  items: LineItemForm[];
  errors: Record<string, string>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: <K extends keyof LineItemForm>(index: number, key: K, value: LineItemForm[K]) => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-base-content">Invoice Line Items</h2>

      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="border border-base-300 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-3">
                <Field
                  label="Description"
                  id={`line-${idx}-desc`}
                  required
                  error={errors[`lineItems.${idx}.description`]}
                >
                  <input
                    id={`line-${idx}-desc`}
                    type="text"
                    className="input input-bordered input-sm w-full"
                    value={item.description}
                    onChange={(e) => onChange(idx, 'description', e.target.value)}
                  />
                </Field>
              </div>
              <Field
                label="Qty"
                id={`line-${idx}-qty`}
                required
                error={errors[`lineItems.${idx}.quantity`]}
              >
                <input
                  id={`line-${idx}-qty`}
                  type="number"
                  min={1}
                  step={1}
                  className="input input-bordered input-sm w-full"
                  value={item.quantity}
                  onChange={(e) =>
                    onChange(idx, 'quantity', e.target.value === '' ? '' : Number(e.target.value))
                  }
                />
              </Field>
              <div className="md:col-span-2">
                <Field
                  label="Unit Price"
                  id={`line-${idx}-price`}
                  required
                  error={errors[`lineItems.${idx}.unitPrice`]}
                >
                  <input
                    id={`line-${idx}-price`}
                    type="number"
                    min={0}
                    step={0.01}
                    className="input input-bordered input-sm w-full"
                    value={item.unitPrice}
                    onChange={(e) =>
                      onChange(
                        idx,
                        'unitPrice',
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                  />
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Field label="Tax Rate (%)" id={`line-${idx}-tax`}>
                <input
                  id={`line-${idx}-tax`}
                  type="number"
                  min={0}
                  max={100}
                  className="input input-bordered input-sm w-full"
                  value={item.taxRate ?? ''}
                  onChange={(e) =>
                    onChange(idx, 'taxRate', e.target.value === '' ? '' : Number(e.target.value))
                  }
                />
              </Field>
              <Field label="Discount (%)" id={`line-${idx}-discount`}>
                <input
                  id={`line-${idx}-discount`}
                  type="number"
                  min={0}
                  max={100}
                  className="input input-bordered input-sm w-full"
                  value={item.discountPercentage ?? ''}
                  onChange={(e) =>
                    onChange(
                      idx,
                      'discountPercentage',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Field>
              <Field label="Cost Centre" id={`line-${idx}-cc`}>
                <input
                  id={`line-${idx}-cc`}
                  type="text"
                  className="input input-bordered input-sm w-full"
                  value={item.costCentre ?? ''}
                  onChange={(e) => onChange(idx, 'costCentre', e.target.value)}
                />
              </Field>
              <Field label="PO Number" id={`line-${idx}-po`}>
                <input
                  id={`line-${idx}-po`}
                  type="text"
                  className="input input-bordered input-sm w-full"
                  value={item.poNumber ?? ''}
                  onChange={(e) => onChange(idx, 'poNumber', e.target.value)}
                />
              </Field>
              <Field label="Vendor Code" id={`line-${idx}-vendor`}>
                <input
                  id={`line-${idx}-vendor`}
                  type="text"
                  className="input input-bordered input-sm w-full"
                  value={item.vendorCode ?? ''}
                  onChange={(e) => onChange(idx, 'vendorCode', e.target.value)}
                />
              </Field>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="btn btn-ghost btn-xs text-error gap-1"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onAdd}
        className="btn btn-outline btn-sm w-full gap-1 border-dashed"
      >
        <PlusIcon className="h-4 w-4" />
        Add Line Item
      </button>
    </div>
  );
}

// ── Step 3 ──────────────────────────────────────────────────────────────

function StepTerms({
  form,
  presets,
  errors,
  onPaymentTermChange,
  onCustomDaysChange,
  onChange,
}: {
  form: FormState;
  presets: PaymentTermPreset[];
  errors: Record<string, string>;
  onPaymentTermChange: (id: string) => void;
  onCustomDaysChange: (days: number) => void;
  onChange: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-base-content">Payment Terms & Notes</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Payment Terms" id="payment-terms">
          <select
            id="payment-terms"
            className="select select-bordered select-sm w-full"
            value={form.paymentTermsId ?? ''}
            onChange={(e) => onPaymentTermChange(e.target.value)}
          >
            <option value="">Select payment terms</option>
            {presets.map((preset) => (
              <option key={preset.id} value={preset.id}>
                {preset.name} — {preset.description}
              </option>
            ))}
          </select>
        </Field>

        {form.paymentTermType === PaymentTermType.CUSTOM && (
          <Field
            label="Custom Days Until Due"
            id="custom-days"
            required
            error={errors['paymentTerms.daysUntilDue']}
          >
            <input
              id="custom-days"
              type="number"
              min={0}
              max={365}
              className="input input-bordered input-sm w-full"
              value={form.customDaysUntilDue ?? ''}
              onChange={(e) =>
                onCustomDaysChange(e.target.value === '' ? 0 : Number(e.target.value))
              }
            />
          </Field>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Due Date" id="due-date">
          <input
            id="due-date"
            type="date"
            className="input input-bordered input-sm w-full"
            value={form.dueDate ?? ''}
            onChange={(e) => onChange('dueDate', e.target.value)}
          />
        </Field>
      </div>

      <Field label="Internal Notes" id="notes">
        <textarea
          id="notes"
          rows={3}
          className="textarea textarea-bordered w-full"
          placeholder="Internal notes (not visible to customer)"
          value={form.notes ?? ''}
          onChange={(e) => onChange('notes', e.target.value)}
        />
      </Field>

      <Field label="Customer Notes" id="customer-notes">
        <textarea
          id="customer-notes"
          rows={3}
          className="textarea textarea-bordered w-full"
          placeholder="Notes visible to customer"
          value={form.customerNotes ?? ''}
          onChange={(e) => onChange('customerNotes', e.target.value)}
        />
      </Field>
    </div>
  );
}

// ── Field wrapper ───────────────────────────────────────────────────────

function Field({
  label,
  id,
  required,
  error,
  children,
}: {
  label: string;
  id?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="form-control">
      <label htmlFor={id} className="label py-1">
        <span className="label-text text-sm">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </span>
      </label>
      {children}
      {error && <span className="text-xs text-error mt-1">{error}</span>}
    </div>
  );
}

function formatDateInput(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default InvoiceCreate;
